import React, { useState, useEffect, useMemo } from 'react';
import { Typography } from 'Components/atoms';
import _ from 'lodash';
import './AiServiceConfigurationModal.scss';
import { Modal, Button, Spinner, Form } from 'react-bootstrap';
import { AiOutlineClose } from '@react-icons/all-files/ai/AiOutlineClose';
import { BrowserCoachingAiServicesConfig, genAmpId } from '@ampsec/platform-client';
import { DEFAULT_AI_SERVICES_CONFIG } from '../../pages/BrowserCoachingPage/data';
import { useI18n } from 'Hooks/useI18n';
import { DropdownComponent } from 'Components/molecules';

export type AiServicesConfig = BrowserCoachingAiServicesConfig;
export type AiServiceConfig = AiServicesConfig['services'][number];
export type AiServiceStyle = AiServiceConfig['templates']['APPROVED'];
type EditableAiServiceStyle = AiServiceStyle & {
  hideBrowserCoaching?: boolean;
};

interface AiServiceConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialConfig: AiServicesConfig;
  onSave: (config: AiServicesConfig) => Promise<void>;
}

export const AiServiceConfigurationModal: React.FC<
  AiServiceConfigurationModalProps
> = ({ isOpen, onClose, initialConfig, onSave }) => {
  const { t } = useI18n();
  const [config, setConfig] = useState<AiServicesConfig>({ services: [] });
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<
    AiServiceConfig['currentStatus']
  >('APPROVED');
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      const loadedConfig =
        initialConfig && !_.isEmpty(initialConfig)
          ? _.cloneDeep(initialConfig)
          : { services: [] };

      if (!Array.isArray(loadedConfig.services)) {
        loadedConfig.services = [];
      }

      const allServices = loadedConfig.services;
      const allServiceIds = new Set(allServices.map((s) => s.id));

      DEFAULT_AI_SERVICES_CONFIG.services.forEach((defaultService) => {
        if (!allServiceIds.has(defaultService.id)) {
          allServices.push(_.cloneDeep(defaultService));
        }
      });
      loadedConfig.services = allServices;

      setConfig(loadedConfig);

      if (allServices.length > 0) {
        setSelectedService(allServices[0].id);
        setSelectedTab(allServices[0].currentStatus);
      } else {
        setSelectedService(null);
        setSelectedTab('APPROVED');
      }
    } else {
      setSelectedService(null);
      setSelectedTab('APPROVED');
      setIsSaving(false);
      setIsDirty(false);
    }
  }, [isOpen, initialConfig]);

  const handleAddNewService = () => {
    const newServiceId = genAmpId();

    const baseTemplates = DEFAULT_AI_SERVICES_CONFIG.services[0]?.templates ?? {};
    const emptyTemplates = Object.keys(baseTemplates).reduce(
      (acc, key) => {
        const templateKey = key as 'APPROVED' | 'DISAPPROVED' | 'UNDEFINED';
        const baseTemplate = baseTemplates[templateKey];
        if (!baseTemplate) {
          return acc;
        }
        acc[templateKey] = {
          ..._.cloneDeep(baseTemplate),
          placeholders: { [newServiceId]: '' },
          limpModeText: '',
          bestPracticesHtml: '',
        };
        return acc;
      },
      {} as AiServiceConfig['templates'],
    );

    const newService: AiServiceConfig = {
      id: newServiceId,
      displayName: '',
      pattern: '',
      currentStatus: 'UNDEFINED',
      isNew: true,
      templates: emptyTemplates,
    };

    const newConfig = _.cloneDeep(config);
    newConfig.services.push(newService);
    setConfig(newConfig);

    setSelectedService(newServiceId);
    setSelectedTab('UNDEFINED');
    setIsDirty(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const configToSave = _.cloneDeep(config);
      await onSave(configToSave);
    } catch (e) {
      // Error toast is already shown by the parent's onSave handler.
      // Close the modal so the user is not stuck in a broken Saving state.
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const updateServiceProperty = (
    field: 'displayName' | 'pattern',
    value: string
  ) => {
    if (!selectedService) {
      return;
    }

    const newConfig = _.cloneDeep(config);
    const service = newConfig.services.find((s) => s.id === selectedService);
    if (!service) {
      return;
    }

    service[field] = value;

    setConfig(newConfig);
    setIsDirty(true);
  };

  const updateServiceStyle = (
    field: keyof EditableAiServiceStyle,
    value: any,
    nestedField?: string
  ) => {
    if (!selectedService) {
      return;
    }

    const newConfig = _.cloneDeep(config);
    const serviceIndex = newConfig.services.findIndex((s) => s.id === selectedService);
    if (serviceIndex === -1) {
      return;
    }

    const style = newConfig.services[serviceIndex].templates[
      selectedTab
    ] as EditableAiServiceStyle;

    if (nestedField) {
      if (field === 'colors') {
        style.colors[nestedField as 'border' | 'text'] = value;
      } else if (field === 'placeholders') {
        style.placeholders[nestedField] = value;
      }
    } else {
      (style as any)[field] = value;
    }

    setConfig(newConfig);
    setIsDirty(true);
  };

  const updateServiceStatus = (
    status: AiServiceConfig['currentStatus']
  ) => {
    if (!selectedService) {
      return;
    }

    const newConfig = _.cloneDeep(config);
    const serviceIndex = newConfig.services.findIndex((s) => s.id === selectedService);
    if (serviceIndex === -1) {
      return;
    }

    newConfig.services[serviceIndex].currentStatus = status;
    setConfig(newConfig);
    setSelectedTab(status);
    setIsDirty(true);
  };

  const handleClose = () => {
    setIsDirty(false);
    onClose();
  };

  const currentService = selectedService
    ? config.services.find((s) => s.id === selectedService)
    : null;

  const hideBrowserCoaching = (
    currentService?.templates?.APPROVED as EditableAiServiceStyle | undefined
  )?.hideBrowserCoaching ?? false;
  const inputsDisabled =
    selectedTab === 'APPROVED' && hideBrowserCoaching;

  const isFormValid = useMemo(() => {
    if (!currentService || !selectedService) {
      return true;
    }

    if (currentService.isNew) {
      if (!currentService.displayName?.trim() || !currentService.pattern?.trim()) {
        return false;
      }
      return true;
    }

    const approvedTemplate = currentService.templates
      .APPROVED as EditableAiServiceStyle;
    if (approvedTemplate?.hideBrowserCoaching) {
      return true;
    }

    const template = currentService.templates[selectedTab];

    const placeholderText = template.placeholders[selectedService] || '';
    const limpModeText = template.limpModeText || '';
    const bestPracticesHtml = template.bestPracticesHtml || '';

    return (
      placeholderText.trim() !== '' &&
      limpModeText.trim() !== '' &&
      bestPracticesHtml.trim() !== ''
    );
  }, [currentService, selectedService, selectedTab]);

  if (!isOpen) {
    return null;
  }

  const statusOptions = [
    { id: 'APPROVED', value: 'APPROVED', displayValue: t('AI_SERVICE_CONFIGURATION_STATUS_APPROVED') },
    { id: 'DISAPPROVED', value: 'DISAPPROVED', displayValue: t('AI_SERVICE_CONFIGURATION_STATUS_DISAPPROVED') },
    { id: 'UNDEFINED', value: 'UNDEFINED', displayValue: t('AI_SERVICE_CONFIGURATION_STATUS_UNDEFINED') },
  ];

  const currentStatusDisplayValue = statusOptions.find(opt => opt.value === currentService?.currentStatus)?.displayValue || currentService?.currentStatus || '';

  return (
    <Modal show={isOpen} className="custom-actions-modal ai-service-configuration-modal w-100" centered onHide={handleClose}>
        <style>{`
            .ai-service-configuration-modal .add-new-service-btn {
                margin-top: 1rem;
            }
        `}</style>
        <Modal.Header>
            <Modal.Title>{t('AI_SERVICE_CONFIGURATION_MODAL_TITLE')}</Modal.Title>
            <AiOutlineClose onClick={handleClose} className="close-button" data-testid="close-icon" />
        </Modal.Header>
        <Modal.Body className="p-0 d-flex flex-row overflow-hidden">
            {/* Sidebar */}
            <div className="ai-service-sidebar">
                {config.services.map((service) => (
                    <div
                        key={service.id}
                        className={`ai-service-item-${service.id} ai-service-item ${
                            selectedService === service.id ? 'active' : ''
                        }`}
                        onClick={() => {
                            setSelectedService(service.id);
                            setSelectedTab(service.currentStatus);
                        }}
                    >
                        <Typography variant="body4" className="mb-0">
                            {service.displayName}
                            {service.currentStatus === 'APPROVED' ? ' ✅' : service.currentStatus === 'DISAPPROVED' ? ' 🚫' : ''}
                        </Typography>
                    </div>
                ))}
                <div
                    className="ai-service-item add-new-service-btn"
                    onClick={handleAddNewService}
                    data-testid="add-new-service-btn"
                >
                    <Typography variant="body4" className="mb-0">
                        + {t('AI_SERVICE_CONFIGURATION_ADD_NEW_SERVICE', 'Add New Service')}
                    </Typography>
                </div>
            </div>

            {/* Main Content */}
            <div className="ai-service-content">
                {currentService ? (
                    <>
                        {currentService.isNew ? (
                            <div className="mb-4">
                                <Typography variant="caption1" color="text-high-emphasis" className="mb-1">
                                    {t('AI_SERVICE_CONFIGURATION_SERVICE_NAME_LABEL', 'Service Name')}
                                </Typography>
                                <input
                                    className="form-control"
                                    value={currentService.displayName}
                                    onChange={(e) => updateServiceProperty('displayName', e.target.value)}
                                    placeholder={t('AI_SERVICE_CONFIGURATION_SERVICE_NAME_PLACEHOLDER', 'e.g., My Custom AI')}
                                />
                            </div>
                        ) : (
                            <Typography variant="h4" color="text-high-emphasis" className="mb-4">
                                {currentService.displayName}
                            </Typography>
                        )}

                        <div className="row g-3 mb-4">
                            <div className="col-md-6">
                                <Typography variant="caption1" color="text-high-emphasis" className="mb-1">
                                    {t('AI_SERVICE_CONFIGURATION_URL_PATTERN_LABEL')}
                                </Typography>
                                <input
                                    className="form-control"
                                    value={currentService.pattern}
                                    readOnly={!currentService.isNew}
                                    disabled={!currentService.isNew}
                                    onChange={(e) => updateServiceProperty('pattern', e.target.value)}
                                    placeholder={t('AI_SERVICE_CONFIGURATION_URL_PATTERN_PLACEHOLDER', 'e.g., *.custom-ai.com')}
                                />
                            </div>
                            <div className="col-md-6">
                                <Typography variant="caption1" color="text-high-emphasis" className="mb-1">
                                    {t('AI_SERVICE_CONFIGURATION_CURRENT_STATUS_LABEL')}
                                </Typography>
                                <div style={{ height: '2.5rem' }}>
                                    <DropdownComponent
                                        value={currentStatusDisplayValue}
                                        dropDownItems={statusOptions}
                                        onChange={(val) => updateServiceStatus(val as any)}
                                        isStartCase={false}
                                        variant="filled"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Status Tabs */}
                        <div className="status-tabs">
                            {['APPROVED', 'DISAPPROVED', 'UNDEFINED'].map((tab) => (
                                <div
                                    key={tab}
                                    className={`status-tab ${
                                        selectedTab === tab ? 'active' : ''
                                    }`}
                                    onClick={() =>
                                        setSelectedTab(
                                            tab as 'APPROVED' | 'DISAPPROVED' | 'UNDEFINED'
                                        )
                                    }
                                >
                                    {tab === 'APPROVED' ? t('AI_SERVICE_CONFIGURATION_STATUS_APPROVED') :
                                     tab === 'DISAPPROVED' ? t('AI_SERVICE_CONFIGURATION_STATUS_DISAPPROVED') :
                                     t('AI_SERVICE_CONFIGURATION_STATUS_UNDEFINED')}
                                </div>
                            ))}
                        </div>

                        {/* Form Fields */}
                        <div className="color-config-row">
                            <div className="color-field">
                                <Typography variant="caption1" color="text-high-emphasis" className="mb-1">
                                    {t('AI_SERVICE_CONFIGURATION_BORDER_COLOR_LABEL')}
                                </Typography>
                                <input
                                    type="color"
                                    className="form-control form-control-color"
                                    value={currentService.templates[selectedTab].colors.border}
                                    onChange={(e) => updateServiceStyle('colors', e.target.value, 'border')}
                                    disabled={inputsDisabled}
                                />
                            </div>
                            <div className="color-field">
                                <Typography variant="caption1" color="text-high-emphasis" className="mb-1">
                                    {t('AI_SERVICE_CONFIGURATION_TEXT_COLOR_LABEL')}
                                </Typography>
                                <input
                                    type="color"
                                    className="form-control form-control-color"
                                    value={currentService.templates[selectedTab].colors.text}
                                    onChange={(e) => updateServiceStyle('colors', e.target.value, 'text')}
                                    disabled={inputsDisabled}
                                />
                            </div>
                        </div>

                        {!currentService.isNew && (
                            <div className="mb-3">
                                <Typography variant="caption1" color="text-high-emphasis" className="mb-1">
                                    {t('AI_SERVICE_CONFIGURATION_PLACEHOLDER_TEXT_LABEL')}
                                </Typography>
                                <input
                                    className="form-control"
                                    value={(selectedService && currentService.templates[selectedTab].placeholders[selectedService]) || ''}
                                    onChange={(e) => updateServiceStyle('placeholders', e.target.value, selectedService ?? undefined)}
                                    disabled={inputsDisabled}
                                />
                            </div>
                        )}

                        <div className="mb-3">
                            <Typography variant="caption1" color="text-high-emphasis" className="mb-1">
                                {t('AI_SERVICE_CONFIGURATION_LIMP_MODE_TEXT_LABEL')}
                            </Typography>
                            <input
                                className="form-control"
                                value={currentService.templates[selectedTab].limpModeText}
                                onChange={(e) => updateServiceStyle('limpModeText', e.target.value)}
                                disabled={inputsDisabled}
                            />
                        </div>

                        {!currentService.isNew && (
                            <div className="mb-3">
                                <Typography variant="caption1" color="text-high-emphasis" className="mb-1">
                                    {t('AI_SERVICE_CONFIGURATION_BEST_PRACTICES_HTML_LABEL')}
                                </Typography>
                                <textarea
                                    className="form-control"
                                    value={currentService.templates[selectedTab].bestPracticesHtml}
                                    onChange={(e) => updateServiceStyle('bestPracticesHtml', e.target.value)}
                                    disabled={inputsDisabled}
                                />
                            </div>
                        )}

                        {selectedTab === 'APPROVED' && (
                            <div className="mb-3 mt-4 pt-3 border-top">
                                <Form.Check
                                    type="switch"
                                    id="hide-browser-coaching"
                                    label={t('AI_SERVICE_CONFIGURATION_HIDE_BROWSER_COACHING_LABEL', 'Hide browser coaching for this service')}
                                    checked={hideBrowserCoaching}
                                    onChange={(e) => updateServiceStyle('hideBrowserCoaching', e.target.checked)}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                        <Typography variant="body2">{t('AI_SERVICE_CONFIGURATION_SELECT_SERVICE_MESSAGE')}</Typography>
                    </div>
                )}
            </div>
        </Modal.Body>
        <Modal.Footer>
            {isDirty && (
                <Typography variant="caption1" color="text-medium-emphasis" className="mb-1">
                    {t('AI_SERVICE_CONFIGURATION_CHANGE_DELAY_NOTICE')}
                </Typography>
            )}
            <Button variant="secondary" onClick={handleClose} disabled={isSaving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={isSaving || !isFormValid}>
                {isSaving ? (
                    <>
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                        />
                        Saving...
                    </>
                ) : (
                    'Save'
                )}
            </Button>
        </Modal.Footer>
    </Modal>
  );
};
